import { useCallback, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { getOllieApiError, setUsage } from 'src/features/OllieSlice'
import {
  confirmOllieOrderingProposal,
  correctOllieOrderingProposal,
  interpretOllieOrderingText,
  isOllieOrderingSessionExpiredError,
  ollieOrderingBasketPath,
  startOllieOrderingSession,
} from 'src/utils/ollieOrderingApi'

/**
 * Dashboard typed-order session. Each product search counts one Ollie use.
 * Picks, qty edits, removes, and confirm do not.
 * Confirm writes the stored proposal into the real basket, then opens it.
 * Interpret, picks, qty, and remove do not write the basket or open generic Ollie.
 */
function useOllieOrderingSession(venueId) {
  const history = useHistory()
  const dispatch = useDispatch()
  const [session, setSession] = useState(null)
  const [proposal, setProposal] = useState(null)
  const [sending, setSending] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState(null)
  const [limit, setLimit] = useState(null)
  const [usage, setUsageSnapshot] = useState(null)
  const [confirmNotice, setConfirmNotice] = useState(null)
  const sendingRef = useRef(false)
  const sessionRef = useRef(null)
  const proposalRef = useRef(null)
  const correctionQueue = useRef(Promise.resolve())

  const dismissPaywall = useCallback(() => {
    setLimit(null)
  }, [])

  const send = useCallback(
    async text => {
      const trimmed = String(text || '').trim()
      if (!trimmed || sendingRef.current) {
        return null
      }
      if (!venueId) {
        setError('This venue could not be loaded. Refresh and try again.')
        return null
      }

      sendingRef.current = true
      setSending(true)
      setError(null)
      setLimit(null)

      try {
        let nextSession = sessionRef.current
        if (!nextSession?.id) {
          const started = await startOllieOrderingSession({ venueId })
          nextSession = started.session
          sessionRef.current = nextSession
          setSession(nextSession)
        }

        const interpret = sessionId =>
          interpretOllieOrderingText({
            venueId,
            sessionId,
            text: trimmed,
          })

        let result
        try {
          result = await interpret(nextSession.id)
        } catch (error) {
          if (!isOllieOrderingSessionExpiredError(error)) {
            throw error
          }
          const started = await startOllieOrderingSession({ venueId })
          nextSession = started.session
          sessionRef.current = nextSession
          setSession(nextSession)
          result = await interpret(nextSession.id)
        }

        setProposal(result.proposal)
        proposalRef.current = result.proposal
        if (result.usage) {
          dispatch(setUsage({ usage: result.usage }))
          setUsageSnapshot(result.usage)
        }
        return result.proposal
      } catch (error) {
        const parsed = getOllieApiError(error)
        if (parsed.limitReached) {
          setLimit({
            paywall: parsed.paywall,
            usage: parsed.usage,
            limitType: parsed.limitType,
          })
          return null
        }
        setError(parsed.message || 'Ollie could not build a proposed order')
        return null
      } finally {
        sendingRef.current = false
        setSending(false)
      }
    },
    [dispatch, venueId],
  )

  const runCorrection = useCallback(
    async ({ quiet, ...payload }) => {
      if (!venueId || !sessionRef.current?.id) {
        setError(
          'Ollie needs the proposed order first. Send what you need, then edit.',
        )
        return null
      }

      // A quiet correction is a stepper save: it must not grey out the rest of
      // the proposed order while it travels.
      if (!quiet) setSending(true)
      setError(null)

      try {
        const result = await correctOllieOrderingProposal({
          venueId,
          sessionId: sessionRef.current.id,
          ...payload,
        })
        setProposal(result.proposal)
        proposalRef.current = result.proposal
        return result.proposal
      } catch (error) {
        if (isOllieOrderingSessionExpiredError(error)) {
          sessionRef.current = null
          setSession(null)
        }
        setError(
          getOllieApiError(error).message ||
            'Ollie could not update the proposed order',
        )
        return null
      } finally {
        if (!quiet) setSending(false)
      }
    },
    [venueId],
  )

  /**
   * Corrections queue rather than collide. Clicking + four times quickly used
   * to drop three of the clicks; now each one waits its turn, and Express still
   * only ever applies one change to the stored proposal at a time.
   */
  const applyCorrection = useCallback(
    payload => {
      const next = correctionQueue.current
        .catch(() => null)
        .then(() => runCorrection(payload))
      correctionQueue.current = next.catch(() => null)
      return next
    },
    [runCorrection],
  )

  const pickListCandidate = useCallback(
    async ({ phrase, choice } = {}) => {
      const selected = String(choice || '').trim()
      if (!selected) return null
      return applyCorrection({
        pick: {
          phrase: phrase || null,
          choice: selected,
        },
      })
    },
    [applyCorrection],
  )

  const confirmProposal = useCallback(async () => {
    if (sendingRef.current) {
      return null
    }
    if (!venueId || !sessionRef.current?.id) {
      setError(
        'Ollie needs the proposed order first. Send what you need, then confirm.',
      )
      return null
    }

    sendingRef.current = true
    setConfirming(true)
    setError(null)

    try {
      // Any correction still in the air is part of what the chef is looking
      // at, so the basket write waits for it.
      await correctionQueue.current.catch(() => null)
      const result = await confirmOllieOrderingProposal({
        venueId,
        sessionId: sessionRef.current.id,
        proposalUpdatedAt: proposalRef.current?.updatedAt,
      })

      // Something the chef could see did not make it into the basket: say so
      // here rather than letting them find the gap in the basket later.
      const skipped = Array.isArray(result.skipped) ? result.skipped : []
      if (skipped.length) {
        setProposal(null)
        proposalRef.current = null
        setConfirmNotice({
          written: Array.isArray(result.written) ? result.written : [],
          skipped,
        })
        return result
      }

      history.push(ollieOrderingBasketPath(venueId))
      return result
    } catch (error) {
      if (isOllieOrderingSessionExpiredError(error)) {
        sessionRef.current = null
        setSession(null)
      }
      setError(
        getOllieApiError(error).message ||
          'Ollie could not add this to the basket',
      )
      return null
    } finally {
      sendingRef.current = false
      setConfirming(false)
    }
  }, [history, venueId])

  const editProposalLine = useCallback(
    async ({ phrase, quantity, remove, quiet } = {}) => {
      if (remove) {
        return applyCorrection({
          edit: { phrase: phrase || null, remove: true },
        })
      }
      const amount = Number(quantity)
      if (!Number.isFinite(amount) || amount <= 0) return null
      return applyCorrection({
        quiet,
        edit: { phrase: phrase || null, quantity: amount },
      })
    },
    [applyCorrection],
  )

  const openBasket = useCallback(() => {
    setConfirmNotice(null)
    history.push(ollieOrderingBasketPath(venueId))
  }, [history, venueId])

  return {
    session,
    proposal,
    sending,
    confirming,
    error,
    limit,
    usage,
    confirmNotice,
    openBasket,
    dismissPaywall,
    send,
    pickListCandidate,
    editProposalLine,
    confirmProposal,
  }
}

export default useOllieOrderingSession
